import StandardChartEditor from "features/chartLibrary/components/StandardChartEditor";
import AnalyticsCard, { IAnalyticsCardProps } from "./AnalyticsCard";
import { useCallback, useMemo, useState } from "react";
import { useStandardChart } from "features/chartLibrary/providers/StandardChartsProvider";
import Loading from "Components/Loading/Loading";
import { Alert } from "@mui/material";
import { ApiChartAttribute } from "@incendium/api";
import { exportStandardChart } from "features/chartLibrary/services/chartService";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import { parseIChartToChartPayload } from "features/chartLibrary/utils";
import { base64ToArrayBuffer, downloadCsv } from "Helpers/download";

interface IStandardAnalyticsCardProps extends IAnalyticsCardProps {
  chartKey: string;
  /** Page-level filters applied on top of saved chart attributes. Not editable in the chart editor. */
  staticAttributes?: ApiChartAttribute[];
}

function mergeChartAttributes(
  staticAttributes: ApiChartAttribute[] | undefined,
  userAttributes: ApiChartAttribute[] | undefined,
) {
  return [...(staticAttributes || []), ...(userAttributes || [])];
}

function StandardAnalyticsCard({
  chartKey,
  staticAttributes,
  ...props
}: IStandardAnalyticsCardProps) {
  const { selectedProject } = useSelectedProject();
  const { chartOutput } = useFromToContext();
  const {
    chart: loadedChart,
    loading,
    refresh,
    clearChart,
  } = useStandardChart(chartKey);
  const [standardChartEditorOpen, setStandardChartEditorOpen] = useState(false);

  const onStandardChartEdit = useCallback(async () => {
    setStandardChartEditorOpen(true);
  }, []);

  const { chart, editorChart } = useMemo(() => {
    const userAttributes =
      loadedChart?.attributes ?? props.chart.attributes ?? [];
    const merged = {
      ...props.chart,
      ...(loadedChart || {}),
      chartKey,
      attributes: mergeChartAttributes(staticAttributes, userAttributes),
    };

    return {
      chart: merged,
      editorChart: {
        ...merged,
        attributes: userAttributes,
      },
    };
  }, [loadedChart, props.chart, chartKey, staticAttributes]);

  const onStandardChartExport = useCallback(async () => {
    if (!selectedProject?.id) {
      return;
    }
    const res = await exportStandardChart(
      selectedProject?.id,
      parseIChartToChartPayload(chart),
      {
        ...chartOutput,
      },
    );

    if (res?.result?.result) {
      const buffer = base64ToArrayBuffer(res.result.result);
      downloadCsv(buffer, chart.name || "Chart Export");
    }
  }, [selectedProject?.id, chart, chartOutput]);

  if (loading) {
    return <Loading />;
  }

  if (!chartKey) {
    return (
      <>
        <Alert severity="error">Chart key is required</Alert>
      </>
    );
  }

  return (
    <>
      <AnalyticsCard
        {...props}
        chart={chart}
        onEdit={onStandardChartEdit}
        onExport={onStandardChartExport}
      />
      <StandardChartEditor
        open={standardChartEditorOpen}
        onClose={() => setStandardChartEditorOpen(false)}
        chart={editorChart}
        onSaved={() => {
          refresh();
          setStandardChartEditorOpen(false);
        }}
        onDeleted={() => {
          clearChart();
        }}
      />
    </>
  );
}

export default StandardAnalyticsCard;
